// =============================================================================
// My commission — the sales girl's own view
// =============================================================================
// One page that answers her three questions:
//   1. How far is MY shop from its target (barem), and what's unlocked so far?
//   2. How much have I earned — in every shop I sold at this month (she can
//      switch shops mid-month), plus online / special-model direct commission?
//   3. What did I earn in past months (even at shops I've since left)?

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiGet } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { PageScaffold } from '../../components/PageScaffold';
import { Card } from '../../components/Card';
import { Money } from '../../components/Money';
import { EmptyState } from '../../components/EmptyState';
import { StatusPill } from '../../components/StatusPill';
import { cn } from '../../lib/cn';

interface MyShop {
  shop: { id: string; code: string; name: string; type: string };
  isHome: boolean;
  teamSize: number;
  netSales: string;
  qualified: boolean;
  qualifyingMin: string;
  commission: string;
  distributed: string;
  currentTierIndex: number;
  nextThreshold: string | null;
  nextRatePct: string | null;
  amountToNext: string | null;
  tiers: Array<{ threshold: string; ratePct: string }>;
  my: {
    rawNet: string;
    fullNet: string;
    promoNet: string;
    weightedNet: string;
    share: string;
    orderCount: number;
  };
}

interface MyDirect {
  kind: 'special' | 'online' | 'cross_sale';
  orderId: string;
  orderNumber: string;
  confirmedAt: string | null;
  label: string;
  productName: string;
  basis: string;
  pct: string;
  halved: boolean;
  commission: string;
  payable: boolean;
  pendingReason: string | null;
  shopCode: string | null;
}

interface MyCommissionData {
  month: string;
  period: { from: string; to: string };
  shops: MyShop[];
  direct: MyDirect[];
  totals: {
    poolShare: string;
    online: string;
    crossSale: string;
    specialPayable: string;
    specialPending: string;
    payableTotal: string;
    total: string;
  };
  history: Array<{
    month: string;
    poolShare: string;
    direct: string;
    total: string;
    perShop: Array<{ shopId: string; shopCode: string | null; rawNet: string; share: string }>;
  }>;
}

function monthOptions(count = 12): Array<{ key: string; label: string }> {
  const out: Array<{ key: string; label: string }> = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    out.push({
      key: `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleDateString('en-MU', { month: 'long', year: 'numeric', timeZone: 'UTC' }),
    });
  }
  return out;
}

function monthLabel(key: string): string {
  const [y, m] = key.split('-').map(Number);
  return new Date(Date.UTC(y!, (m ?? 1) - 1, 1)).toLocaleDateString('en-MU', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function fmtPct(rate: string): string {
  return `${Number((Number(rate) * 100).toFixed(6))}%`;
}

export function MyCommissionPage(): JSX.Element {
  const { user } = useAuth();
  const months = monthOptions();
  const [month, setMonth] = useState(months[0]!.key);
  const data = useQuery({
    queryKey: ['my-commission', user?.id, month],
    queryFn: () => apiGet<MyCommissionData>('/finance/commission/me', { month }),
  });
  // The discount limit (cap) drives the half-commission rule; show the real value.
  const company = useQuery({
    queryKey: ['company'],
    queryFn: () => apiGet<{ salesDiscountCapPct: string }>('/company'),
  });
  const capLabel = company.data ? fmtPct(company.data.salesDiscountCapPct) : '3%';

  const d = data.data;
  const pendingSpecial = Number(d?.totals.specialPending ?? 0);

  return (
    <PageScaffold
      title="My commission"
      subtitle="Your shop pool is shared pro-rata by what each girl sells — promo / discounted sales count half. Special models and online sales pay you directly. If your shop misses its target you still earn 5% on the cross-sales accessories you sold on any invoice that also includes a mattress. All sales figures shown here include VAT."
      actions={
        <label className="flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-wider text-ink-3">Month</span>
          <select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="kc-input h-10 text-sm w-auto"
          >
            {months.map((m) => (
              <option key={m.key} value={m.key}>{m.label}</option>
            ))}
          </select>
        </label>
      }
    >
      <CommissionExplainer capLabel={capLabel} />
      {data.isLoading || !d ? (
        <div className="text-center text-ink-3 py-12">Loading your commission…</div>
      ) : (
        <>
          {/* Totals */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-accent-soft border-accent/20">
              <div className="text-[10px] uppercase tracking-wider text-accent font-semibold">This month (est.)</div>
              <Money value={d.totals.total} className="kc-serif text-3xl text-accent mt-2 block" />
              {pendingSpecial > 0 && (
                <div className="text-[11px] text-accent/80 mt-1">
                  incl. <Money value={d.totals.specialPending} muted /> awaiting payment + delivery
                </div>
              )}
            </Card>
            <Hero label="Shop pool shares" value={<Money value={d.totals.poolShare} />} hint="pro-rata of your sales, all shops" />
            <Hero label="Online sales" value={<Money value={d.totals.online} />} hint="per-category rate, ½ on promo" />
            <Hero
              label="Special models"
              value={<Money value={String(Number(d.totals.specialPayable) + Number(d.totals.specialPending))} />}
              hint={pendingSpecial > 0 ? 'paid once delivered + fully paid' : 'Torres, Swisspedic, Memory Gel…'}
            />
          </div>

          {/* Per-shop goal + share */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {d.shops.map((s) => (
              <ShopGoalCard key={s.shop.id} s={s} />
            ))}
            {d.shops.length === 0 && (
              <Card>
                <EmptyState
                  title="No shop sales this month"
                  description="Your shop target appears here as soon as you're assigned to a shop or make a sale."
                />
              </Card>
            )}
          </div>

          {/* Direct entries */}
          <Card
            title="Direct commission"
            subtitle="Earned per sale — not part of a shop pool: special models, online sales, and cross-sales accessories (5% when your shop misses target)"
            bodyClassName="p-0"
          >
            {d.direct.length === 0 ? (
              <EmptyState title="None this month" description="Special models (e.g. Torres, Swisspedic, Memory Gel), online sales and cross-sales accessories appear here." />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
                      <th className="px-6 py-2 font-medium">Order</th>
                      <th className="px-3 py-2 font-medium">What</th>
                      <th className="px-3 py-2 font-medium text-right">Sale (incl. VAT)</th>
                      <th className="px-3 py-2 font-medium text-right">Rate</th>
                      <th className="px-3 py-2 font-medium text-right">You earn</th>
                      <th className="px-6 py-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.direct.map((e, i) => (
                      <tr key={`${e.orderId}-${i}`} className="border-b border-rule-2 hover:bg-paper-2">
                        <td className="px-6 py-2.5">
                          <Link to={`/orders/${e.orderId}`} className="kc-mono text-xs text-ink hover:text-accent hover:underline">
                            {e.orderNumber}
                          </Link>
                          <div className="text-[10px] text-ink-4">
                            {e.shopCode ?? ''}{e.confirmedAt ? ` · ${new Date(e.confirmedAt).toLocaleDateString('en-MU', { day: 'numeric', month: 'short' })}` : ''}
                          </div>
                        </td>
                        <td className="px-3 py-2.5">
                          <StatusPill tone={e.kind === 'special' ? 'accent' : e.kind === 'cross_sale' ? 'warning' : 'info'}>
                            {e.kind === 'special' ? e.label : e.kind === 'cross_sale' ? 'cross-sale' : 'online'}
                          </StatusPill>
                          <div className="text-[11px] text-ink-3 mt-0.5 truncate max-w-[200px]">{e.productName}</div>
                        </td>
                        <td className="px-3 py-2.5 text-right"><Money value={e.basis} muted /></td>
                        <td className="px-3 py-2.5 text-right kc-mono text-xs">
                          {fmtPct(e.pct)}
                          {e.halved && <span className="text-warning" title="Half rate — promo/discounted sale"> ×½</span>}
                        </td>
                        <td className="px-3 py-2.5 text-right"><Money value={e.commission} className="font-medium" /></td>
                        <td className="px-6 py-2.5">
                          {e.payable ? (
                            <StatusPill tone="positive">earned</StatusPill>
                          ) : (
                            <StatusPill tone="warning">after {e.pendingReason}</StatusPill>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* History */}
          <Card title="Past months" subtitle="Everything you earned — including shops you've since moved from" bodyClassName="p-0">
            {d.history.every((h) => Number(h.total) === 0) ? (
              <EmptyState title="No earlier commission yet" />
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
                    <th className="px-6 py-2 font-medium">Month</th>
                    <th className="px-3 py-2 font-medium">Shops</th>
                    <th className="px-3 py-2 font-medium text-right">Pool share</th>
                    <th className="px-3 py-2 font-medium text-right">Direct</th>
                    <th className="px-6 py-2 font-medium text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {d.history.map((h) => (
                    <tr key={h.month} className="border-b border-rule-2 hover:bg-paper-2">
                      <td className="px-6 py-2.5 text-ink">{monthLabel(h.month)}</td>
                      <td className="px-3 py-2.5">
                        <div className="flex flex-wrap gap-1">
                          {h.perShop.length === 0 ? (
                            <span className="text-ink-4 text-xs">—</span>
                          ) : (
                            h.perShop.map((p) => (
                              <span key={p.shopId} className="text-[10px] kc-mono bg-paper-2 border border-rule rounded px-1.5 py-0.5 text-ink-3">
                                {p.shopCode ?? '?'} · <Money value={p.share} muted />
                              </span>
                            ))
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-right"><Money value={h.poolShare} muted /></td>
                      <td className="px-3 py-2.5 text-right"><Money value={h.direct} muted /></td>
                      <td className="px-6 py-2.5 text-right"><Money value={h.total} className="font-medium" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </>
      )}
    </PageScaffold>
  );
}

// ── Shop goal card — her target progress + her cut at one shop ───────────────

function ShopGoalCard({ s }: { s: MyShop }): JSX.Element {
  const net = Number(s.netSales);
  const qualifyingMin = Number(s.qualifyingMin);
  const idx = s.currentTierIndex;
  const earning = s.qualified && idx >= 0;
  const belowMin = !s.qualified && qualifyingMin > 0;
  const topReached = earning && idx === s.tiers.length - 1;
  const promo = Number(s.my.promoNet);

  const lower = earning && s.tiers[idx] ? Number(s.tiers[idx]!.threshold) : 0;
  const upper = belowMin
    ? qualifyingMin
    : s.nextThreshold != null
      ? Number(s.nextThreshold)
      : s.tiers.length > 0
        ? Number(s.tiers[s.tiers.length - 1]!.threshold)
        : 0;
  const segPct = topReached ? 100 : upper > lower ? Math.min(100, Math.max(0, ((net - lower) / (upper - lower)) * 100)) : 0;

  return (
    <Card bodyClassName="p-0" className="overflow-hidden">
      <div className="p-5 bg-accent-soft">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-accent font-semibold">
              {s.shop.name}
              {s.isHome && <span className="ml-1.5 normal-case tracking-normal">· your shop</span>}
            </div>
            <Money value={s.netSales} className="kc-serif text-3xl text-accent mt-1 block" />
            <div className="text-[11px] text-accent/70 mt-0.5">shop sales this month (incl. VAT)</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-accent/70 font-semibold">Pool unlocked</div>
            <Money value={s.commission} className="text-xl text-accent block mt-1" />
          </div>
        </div>

        {s.tiers.length > 0 ? (
          <>
            <div className="mt-3 h-2 bg-white/50 rounded-full overflow-hidden">
              <div
                className={cn('h-full rounded-full transition-all duration-500', topReached ? 'bg-positive' : 'bg-accent')}
                style={{ width: `${segPct}%` }}
              />
            </div>
            <div className="text-xs text-accent/90 mt-1.5">
              {topReached ? (
                <span className="font-medium">Top tier reached 🎉</span>
              ) : belowMin ? (
                <>
                  <Money value={String(Math.max(0, qualifyingMin - net))} className="text-accent/90" /> more shop sales to
                  start earning
                </>
              ) : (
                <>
                  <Money value={s.amountToNext ?? '0'} className="text-accent/90" /> more to
                  {idx < 0 ? ' start earning ' : ' reach '}
                  <span className="font-medium">{fmtPct(s.nextRatePct ?? '0')}</span>
                </>
              )}
            </div>
          </>
        ) : (
          <div className="text-xs text-accent/80 mt-2">No barem configured for this shop yet.</div>
        )}
      </div>

      <div className="grid grid-cols-2 divide-x divide-rule-2 border-t border-rule-2">
        <div className="p-4">
          <div className="text-[10px] uppercase tracking-wider text-ink-3 font-semibold">Your sales here (incl. VAT)</div>
          <Money value={s.my.rawNet} className="text-lg text-ink mt-1 block" />
          <div className="text-[11px] text-ink-3 mt-0.5">
            {s.my.orderCount} order{s.my.orderCount === 1 ? '' : 's'}
            {promo > 0 && (
              <span title="Promo-priced or over-cap discounted sales earn half commission">
                {' '}· <Money value={s.my.promoNet} muted /> half-rate (½)
              </span>
            )}
          </div>
        </div>
        <div className="p-4">
          <div className="text-[10px] uppercase tracking-wider text-ink-3 font-semibold">Your share</div>
          <Money value={s.my.share} className="text-lg text-accent font-medium mt-1 block" />
          <div className="text-[11px] text-ink-3 mt-0.5">
            {earning ? 'pro-rata of your sales' : 'once the shop qualifies'}
          </div>
        </div>
      </div>
    </Card>
  );
}

// ── Plain-language "how is this calculated" breakdown for the sales team ──────

function CommissionExplainer({ capLabel }: { capLabel: string }): JSX.Element {
  return (
    <Card bodyClassName="p-0">
      <details open className="group">
        <summary className="flex items-center justify-between cursor-pointer list-none px-5 py-3.5 hover:bg-paper-2">
          <span className="text-sm font-medium text-ink">How your commission is calculated</span>
          <span className="text-ink-3 text-lg leading-none group-open:hidden">＋</span>
          <span className="text-ink-3 text-lg leading-none hidden group-open:inline">＝</span>
        </summary>
        <div className="px-5 pb-5 pt-1 border-t border-rule-2">
          <ul className="space-y-2.5 text-sm text-ink-2">
            <li>
              <span className="text-ink font-medium">Everything is on the sale price incl. VAT.</span> Every figure on
              this page — your shop's turnover, your sales, and every commission — is worked out on the full price the
              customer paid, VAT included.
            </li>
            <li>
              <span className="text-ink font-medium">Shop target ladder (barem).</span> The more your whole shop sells
              in the month, the bigger the commission pool it unlocks. Nothing is earned until the shop passes its
              qualifying minimum.
            </li>
            <li>
              <span className="text-ink font-medium">The pool is split by what each girl sold.</span> Sell 40% of the
              shop's sales, get 40% of the pool — you never take the whole pool alone.
            </li>
            <li>
              <span className="text-ink font-medium">Full-price sales count in full.</span>
            </li>
            <li>
              <span className="text-ink font-medium">Half-commission sales</span> count for half of your share. A sale
              is half when it's sold at a <span className="text-ink">promo price</span>, or{' '}
              <span className="text-ink">discounted more than {capLabel}</span> (the limit that needs the owner's
              approval). A discount of {capLabel} or less is your own allowance and still counts in full — and only the
              discounted item is affected, not the rest of the order.
            </li>
            <li>
              <span className="text-ink font-medium">Special models (Torres, Swisspedic, Memory Gel…)</span> pay you 2%
              of the sale price (incl. VAT) directly, on top of the pool — but only once the order is fully paid and
              delivered.
            </li>
            <li>
              <span className="text-ink font-medium">Cross-sales accessories (protectors, pillows, toppers, duvets,
              bed sheets…).</span> Every mattress sale should pull accessories. If your shop misses its target so the
              pool stays locked, you still earn <span className="text-ink">5%</span> on the eligible cross-sales you
              sold — but only on an invoice that also has a mattress on it (accessories sold on their own don't count).
              A reward for product knowledge even on a slow month.
            </li>
            <li>
              <span className="text-ink font-medium">Returns &amp; gifts.</span> A returned item is taken off the sale
              it came from; free gifts earn nothing.
            </li>
            <li>
              <span className="text-ink font-medium">Selling at several shops?</span> Each shop is judged on its own
              target, and all your shares add up here.
            </li>
          </ul>
        </div>
      </details>
    </Card>
  );
}

function Hero({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }): JSX.Element {
  return (
    <Card>
      <div className="text-[10px] uppercase tracking-wider text-ink-3 font-semibold">{label}</div>
      <div className="text-2xl font-medium text-ink mt-2">{value}</div>
      {hint && <div className="text-xs text-ink-3 mt-1.5">{hint}</div>}
    </Card>
  );
}
